import React, { useCallback, useEffect, useState } from "react";
import HeaderPanel from "../layout/HeaderPanel";
import FooterPanel from "../layout/FooterPanel";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { changeDocumentPartStructureAsync, DocumentPartItem } from "../../redux/feature/document/document-slice";
import { formatMessage, formatMessageWithParams } from "../../utils/helpers/LanguageHelper";
import { WithApiFileLocationProvider } from "../document/WithApiFileLocationProvider";
import { fetchDocumentRoots } from "../../api/document/Location";
import { FileLocation } from "../document/FileLocationProvider";
import { DocumentLocationExplorer } from "../document/DocumentLocationExplorer";
import { FlatPathTreeItem } from "../generic/FlatPathTreeExplorer";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import { ExportDocumentPanel } from "../document/ExportDocumentPanel";
import { DocumentOutline } from "../document/DocumentOutline";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/hooks";
import {
  selectBackendUrl,
  selectClientId,
  selectCpmUsername,
  selectUserFunctions,
  setIxbrlSettings,
  setSeahorseDocumentLinked,
} from "../../redux/feature/settings/configurations-slice";
import { CHANGE_DOC_STRUCTURE_NOTIFICATION } from "../../utils/constants/notification";
import { FindItem, useFilteredElaborations } from "../generic/GenericElaborationSelector";
import { ElaborationWrapper } from "../coobjects/ElaborationWrapper";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../../utils/constants/eventChannel";
import { PageDocumentRoots } from "../../api/models/page-document-roots";
import { fetchAzureDefaultSiteFromExternalConfig, fetchDocumentPartDetails } from "../../api/document/DocumentsPart";
import { SearchBar } from "../generic/searchBar/SearchBar";
import { DBMapping } from "../document/DBMapping";
import { ManageStructure } from "../document/ManageStructure";
import { DeleteDocumentHelper } from "../../utils/helpers/DeleteDocumentsHelper";
import { fetchIxbrlSettings, fetchSeahorseDocumentLinked } from "../../pages/seahorse/SeahorseUtils";
import { store } from "../../redux/store/store";
import { isNotAuthorizedUser } from "../../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../../api/models/user-function-enum";
import { UserFunction } from "../../api/models/user-function";
import { DocumentPartWithStatus } from "../../api/models/document-part-with-status";
import { elaborationStatusItemIsBlocking } from "../../redux/feature/object/remote-elaborations-slice";

interface DocumentListWrapperState {
  loaded: boolean;
  selectedTreeItem: FlatPathTreeItem | undefined;
  currentOperationItemId: string | undefined;
  page: "folders" | "outline" | "export" | "dbmapping" | "managestructure";
  treeItems: any;
  modifiedFields: boolean;
  deleteItem: DocumentPartItem;
}

const documentListWrapperInitialState: DocumentListWrapperState = {
  loaded: true,
  selectedTreeItem: undefined,
  currentOperationItemId: undefined,
  page: "folders",
  treeItems: [],
  modifiedFields: false,
  deleteItem: null,
};

export interface DocumentListProps {
  idPartRoot?: string;
}

const SITE = "/sites/";

export const DocumentsList: React.FunctionComponent<DocumentListProps> = (props) => {
  const [state, setState] = React.useState<DocumentListWrapperState>(documentListWrapperInitialState);
  const [pagination, setPagination] = React.useState<PageDocumentRoots>();
  const username = useAppSelector(selectCpmUsername);
  const backendUrl = useAppSelector(selectBackendUrl);
  const clientId = useAppSelector(selectClientId);
  const [azureDefaultSite, setAzureDefaultSite] = React.useState("");
  const [sharePointHostName, setSharePointHostName] = React.useState("");
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState(null);
  const userFunctions: UserFunction[] = useAppSelector(selectUserFunctions);
  const notAuthorizedUserForIXBRL =
    isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_ADMIN, userFunctions) &&
    isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_USER, userFunctions);
  const [documentPartWithStatus, setDocumentPartWithStatus] = useState<DocumentPartWithStatus[]>([]);

  useEffect(() => {
    if (searchQuery || searchQuery === "") {
      setState({
        ...state,
        loaded: false,
        page: "folders",
      });
    }
  }, [searchQuery]);

  async function onSelectItem(item: FlatPathTreeItem | undefined) {
    if (!item?.document || item.document.folder) setState({ ...state, selectedTreeItem: undefined });
    else {
      if (item.document.root) {
        item.document = await fetchDocumentPartDetails(item.document.id);
      }
      setState({ ...state, selectedTreeItem: item, page: "outline" });
    }
  }

  let timer;
  const showOutline =
    state.selectedTreeItem?.document && !state.selectedTreeItem.document.folder && state.page === "outline";
  function handleChangeSearch(event: { value: string }) {
    // eslint-disable-next-line no-undef
    clearTimeout(timer);
    // eslint-disable-next-line no-undef
    timer = setTimeout(() => {
      setSearchQuery(event.value.trim());
    }, 1000);
  }

  function updateTreeItems(treeItems: any, modifiedFields: boolean) {
    setState({ ...state, treeItems, modifiedFields });
  }

  const changeDocumentPartStructure = async () => {
    await changeDocumentPartStructureAsync({
      channelIdToNotify: props.idPartRoot ?? EVENT_CHANNEL_DEFAULT_TOPIC,
      username,
      structureDocumentPart: state.treeItems,
      documentId: state.selectedTreeItem.document.id,
      dispatch,
      backendUrl,
      clientId,
    });
    setState({
      ...state,
      loaded: false,
      modifiedFields: false,
    });
  };

  useEffect(() => {
    fetchAzureDefaultSiteFromExternalConfig().then((resAzureDefaultSite) => {
      if (resAzureDefaultSite) {
        setAzureDefaultSite(resAzureDefaultSite.azureDefaultSite);
        setSharePointHostName(resAzureDefaultSite.sharePointHostName);
      }
    });
  }, []);

  const changePage = (page: "dbmapping" | "managestructure" | "outline") => {
    setState((s) => ({
      ...s,
      page: page,
    }));
  };

  const goToOutline = async () => {
    setState((s) => ({
      ...s,
      page: "outline",
      loaded: false,
    }));
  };

  const addedElaborationsDetails = useAppSelector((store) => store.remoteElaborations.addedElaborationItemsList);
  const documentElabFindFunction = useCallback(
    (item: FindItem) =>
      item.elaborationType !== "SHEET_AND_AREA" && elaborationStatusItemIsBlocking(item, addedElaborationsDetails),
    [addedElaborationsDetails]
  );
  const blockingElaboration = useFilteredElaborations({
    findFunction: documentElabFindFunction,
  });

  if (state.page === "export") {
    return (
      <ExportDocumentPanel
        documentId={state.currentOperationItemId}
        onCloseModal={goToOutline}
        channelIdToNotify={props.idPartRoot}
      />
    );
  } else if (state.page === "dbmapping") {
    return <DBMapping selectedTreeItem={state.selectedTreeItem} onBackButtonClick={goToOutline} />;
  } else if (state.page === "managestructure") {
    return (
      <ElaborationWrapper notificationKey={CHANGE_DOC_STRUCTURE_NOTIFICATION} elaborationItem={blockingElaboration}>
        <ManageStructure
          changeDocumentPartStructure={changeDocumentPartStructure}
          selectedTreeItem={state.selectedTreeItem}
          updateTreeItems={updateTreeItems}
          modifiedFields={state.modifiedFields}
          onBackButtonClick={goToOutline}
          elaborationItem={blockingElaboration}
        />
      </ElaborationWrapper>
    );
  }
  return (
    <div style={{ marginBottom: "-2.5rem" }}>
      <HeaderPanel applyPadding>
        {!showOutline
          ? formatMessage("cof.opendocuments.library")
          : formatMessageWithParams("cof.file.open.outline.title.documentoutline", {
              documentName: state.selectedTreeItem.document.name.replace(/\.docx$/, ""),
            })}
      </HeaderPanel>
      {!showOutline && <SearchBar onChangeValue={handleChangeSearch}></SearchBar>}
      <div className="ms-Grid" dir="ltr">
        <WithApiFileLocationProvider
          selectDepth="ITEM"
          cleanState={!state.loaded}
          getItems={async () => {
            if (!notAuthorizedUserForIXBRL) {
              fetchIxbrlSettings().then((result) => store.dispatch(setIxbrlSettings(result)));
              fetchSeahorseDocumentLinked().then((result) => store.dispatch(setSeahorseDocumentLinked(result)));
            }
            const page = "0";
            const embeddedRoots = await fetchDocumentRoots(searchQuery, page);
            setState((s) => ({ ...s, loaded: true }));
            setPagination(embeddedRoots.page);
            return embeddedRoots._embedded.content.map<FileLocation>((df) => {
              return {
                key: df.documentPart.driveItemId,
                text: df.documentPart.name.replace(/\.docx$/, ""),
                iconName: "WordLogoInverse",
                document: df.documentPart,
                readPermission: df.hasReadPermissions,
              };
            });
          }}
          pagination={pagination}
        >
          {!showOutline && (
            <DocumentLocationExplorer
              onSelectItem={onSelectItem}
              page={"OPEN"}
              searchQuery={searchQuery}
              showDownloadButton={true}
            />
          )}
        </WithApiFileLocationProvider>

        {showOutline && (
          <ElaborationWrapper notificationKey={CHANGE_DOC_STRUCTURE_NOTIFICATION} elaborationItem={blockingElaboration}>
            <DocumentOutline
              isLibraryRibbon={true}
              isOpenOutlineRibbon={true}
              selectedTreeItem={state.selectedTreeItem}
              idPartRoot={props.idPartRoot}
              onDeleteDocument={(item) => setState((s) => ({ ...s, deleteItem: (item as any).data }))}
              onExportDocument={(item) =>
                setState((s) => ({
                  ...s,
                  currentOperationItemId: item.key,
                  page: "export",
                }))
              }
              updateTreeItems={updateTreeItems}
              changePage={changePage}
              documentPartWithStatus={documentPartWithStatus}
              setDocumentPartWithStatus={setDocumentPartWithStatus}
            />
          </ElaborationWrapper>
        )}

        <DeleteDocumentHelper
          partRoot={state.selectedTreeItem?.document}
          onCancelDelete={() => setState((s) => ({ ...s, deleteItem: null, loaded: false, page: "folders" }))}
          deleteItem={state.deleteItem}
        />

        <FooterPanel>
          {showOutline && (
            <PrimaryButton
              text={formatMessage("cof.opendocuments.library")}
              onClick={() => {
                setSearchQuery("");
                setState((s) => ({
                  ...s,
                  page: "folders",
                }));
              }}
              disabled={!showOutline && !state.selectedTreeItem}
              style={{
                float: "left",
              }}
              className={ButtonVariants.white}
            />
          )}
          {!showOutline && (
            <a
              style={{ float: "right" }}
              href={
                "https://" +
                sharePointHostName +
                (!azureDefaultSite?.includes("sites/") && !azureDefaultSite?.includes("teams/") ? SITE : "") +
                azureDefaultSite
              }
              target="_blank"
              rel="noreferrer"
            >
              {formatMessage("cof.opendocuments.openinsharepoint")}
            </a>
          )}
        </FooterPanel>
      </div>
    </div>
  );
};
